import React, { FC, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import useCommon from "../../common/hooks/useCommon";
import TransactionDetailsOverview from "./transaction-details-overview";
import InternalTransactionList from "../common/internal-transaction-list";
import TransactionState from "./transaction-state";
import TransactionTokenTransfers from "./transaction-token-transfers";
import TransactionLogs from "./transaction-logs";
import TransactionRowTrace from "./transaction-row-trace";
import { addressService, transactionService } from "../../store/actions";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import BTooltip from "../../components/b-tooltip";

interface Transactions {
  hash: string;
  block: number;
  timestamp: string | number;
  tx_types: any;
  extraMark: string;
  from: any;
  to: any;
  last_seen: any;
  value: string | number;
  gas_price: any;
  gas_used: any;
  is_contract: boolean;
}

const TransactionDetails: FC = () => {
  const location = useLocation();
  const hashSplit = location.hash.split("#");
  const { hash } = useParams();
  const { get0x } = useCommon();
  const [isgetError, setIsgetError] = useState(false);
  const { t, i18n } = useTranslation();
  const [transactionLogs, setTransactionLogs] = useState([]);
  const [tab, setTab] = useState(hashSplit?.[1] ?? "");
  const [tabView, setTabView] = useState({
    tokenTransfers: true,
    internalTransactions: true,
    state: true,
    rawStrace: true,
  });
  const [sender, setSender] = useState("");
  const [transactions, setTransactions] = useState<Transactions[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Fetch transaction details to get sender address
  const getTx = async () => {
    try {
      const response = await transactionService.getTransaction(hash);
      setSender(response?.from?.hash || "");
    } catch (err) {
      console.error("Error fetching transaction:", err);
    }
  };
  const getAllTransactions = async (address: string) => {
    try {
      const response = await addressService.getAddressTransactions(
        address,
        `?limit=50&page=1`
      );
      const txs = response?.items || [];
      setTransactions(txs);

      // Find the current transaction index
      const currentTxIndex = txs.findIndex((tx: any) => tx.hash === hash);
      setCurrentIndex(currentTxIndex);
    } catch (err) {
      console.error("Error fetching sender transactions:", err);
    }
  };

  const nextTx = () => {
    if (currentIndex >= 0 && currentIndex < transactions.length - 1) {
      const nextHash = transactions[currentIndex + 1]?.hash;
      window.location.href = `/tx/${nextHash}`;
    }
  };

  // Navigate to the previous transaction
  const previousTx = () => {
    if (currentIndex > 0) {
      const prevHash = transactions[currentIndex - 1]?.hash;
      window.location.href = `/tx/${prevHash}`;
    }
  };

  const handleTab = (e: string) => {
    setTab(e);
    if (!!e?.length) window.location.hash = `#${e}`;
    else {
      window.location.hash = "";
      // @ts-ignore
      window.history.replaceState(null, null, " ");
    }
  };
  const getTransactionLogs = async () => {
    try {
      transactionService
        .getTransactionLogs(hash)
        .then((response) => {
          setTransactionLogs(response?.items);
        })
        .catch((e) => "");
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    getTransactionLogs();
    getTx();

    const validTabs = [
      "",
      "internaltx",
      "tokenTransfer",
      "logs",
      "statechange",
      "rawTrace",
    ];
    if (!validTabs.includes(tab)) {
      setTab("");
      window.location.hash = "";
    }
  }, [tab]);
  useEffect(() => {
    if (sender) {
      getAllTransactions(sender);
    }
  }, [sender]);

  return (
    <div className="py-3">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="bs-page-title d-flex align-items-center justify-content-start gap-2">
              <div className="text-nowrap">{t("Transaction Details")}</div>

              <div className="bs-list-content">
                <button
                  className="bg-secondary bg-opacity-10 hover:bg-opacity-30 border border-secondary border-opacity-10 text-dark  rounded-2 m-lg-1"
                  onClick={previousTx}
                  disabled={currentIndex >= transactions.length - 1}
                >
                  <BTooltip text={`${t("View Previous Transaction")}`}>
                    <i className="bi bi-chevron-left fs-6"></i>
                  </BTooltip>
                </button>

                <button
                  className=" bg-secondary bg-opacity-10 hover:bg-opacity-30 border border-secondary border-opacity-10 text-dark  rounded-2"
                  onClick={nextTx}
                  disabled={currentIndex >= transactions.length - 1}
                >
                  <BTooltip text={`${t("View Next Transaction")}`}>
                    <i className="bi bi-chevron-right fs-6 "></i>
                  </BTooltip>
                </button>
              </div>
            </div>
          </div>
        </div>

        {isgetError ? (
          <React.Fragment>
            <div className="bs-card mb-3">
              <div className="bs-card-body text-center py-28">
                <div className="mb-4">
                  <span className="bg-warning bg-opacity-10 border border-warning border-opacity-25 text-yellow-600 fs-70x content-center rounded-pill mx-auto content-not-found">
                    <i className="bi bi-file-earmark-x fs-4 text-yellow-600"></i>
                  </span>
                </div>
                <h2 className="page-error-title">
                  Sorry, We are unable to locate this TxnHash
                </h2>
                <p className="text-muted mb-0">{get0x(hash)}</p>
              </div>
              <div className="bs-card-footer">
                <div className="alert alert-dark mt-3" role="alert">
                  <div className="d-flex align-items-baseline gap-2">
                    <i className="bi bi-info-circle-fill"></i>
                    <span>
                      OpenScan.ai only provides an overview of the current state
                      of the blockchain such as your transaction status but we
                      have no control over these transactions
                    </span>
                  </div>
                </div>
                <ol
                  className="list-xs-space mb-0 ps-4 transaction-not-fount-ol"
                  type="1"
                >
                  <li>
                    If you have just submitted a transaction please wait for at
                    least 30 seconds before refreshing this page.
                  </li>
                  <li>
                    It could still be in the TX Pool of a different node,
                    waiting to be broadcasted.
                  </li>
                  <li>
                    When the network is busy it can take a while for your
                    transaction to propagate through the network and for us to
                    index it.
                  </li>
                  <li>
                    If it still does not show up after 1 hour, please check with
                    your sender/exchange/wallet/transaction provider for
                    additional information.
                  </li>
                </ol>
              </div>
            </div>
          </React.Fragment>
        ) : (
          <div className="row">
            <div className="col-lg-12">
              <div className="bs-tab d-flex justify-content-between align-items-center">
                <div>
                  <button
                    className={`bs-tab-item ${
                      [""].includes(tab) ? "active" : ""
                    }`}
                    onClick={() => handleTab("")}
                  >
                    {t("Overview")}
                  </button>
                  {tabView.tokenTransfers && (
                    <button
                      className={`bs-tab-item ${
                        ["tokenTransfer"].includes(tab) ? "active" : ""
                      }`}
                      onClick={() => handleTab("tokenTransfer")}
                    >
                      {t("Token transfers")}
                    </button>
                  )}
                  <button
                    className={`bs-tab-item ${
                      ["internaltx"].includes(tab) ? "active" : ""
                    }`}
                    onClick={() => handleTab("internaltx")}
                  >
                    {t("Internal Transactions")}
                  </button>
                  {!!transactionLogs?.length && (
                    <button
                      className={`bs-tab-item ${
                        ["logs"].includes(tab) ? "active" : ""
                      }`}
                      onClick={() => handleTab("logs")}
                    >
                      {t("Logs")}{" "}
                      {transactionLogs?.length > 0
                        ? `(${transactionLogs?.length})`
                        : ""}
                    </button>
                  )}
                  <button
                    className={`bs-tab-item ${
                      ["statechange"].includes(tab) ? "active" : ""
                    }`}
                    onClick={() => handleTab("statechange")}
                  >
                    {t("State")}
                  </button>
                  <button
                    className={`bs-tab-item ${
                      ["rawTrace"].includes(tab) ? "active" : ""
                    }`}
                    onClick={() => handleTab("rawTrace")}
                  >
                    {t("Raw trace")}
                  </button>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Link
                    to={`https://docs.blocksscan.io/api-endpoints/geth-parity-proxy#eth_gettransactionbyhash-wip`}
                  >
                    <button
                      type="button"
                      className={`btn d-inline-flex align-items-center bg-white border px-2 py-1 `}
                    >
                      <span className="me-1 fw-light">&lt;/&gt;</span>
                      <span className="fw-medium">API</span>
                    </button>
                  </Link>
                  <Dropdown className="d-flex bs_token-social">
                    <Dropdown.Toggle
                      id="dropdown-button-dark-example1"
                      variant="white"
                      size="sm"
                    >
                      <i className="bi bi-list-check"> </i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="dark" style={{ minWidth: "12rem" }}>
                      <Dropdown.Item
                        href="#"
                        className="bs-small-font"
                        target="_blank"
                      >
                        todo: Validate Transaction
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        href="#"
                        className="bs-small-font"
                        rel="nofollow"
                      >
                        todo: Geth Debug Trace
                      </Dropdown.Item>
                      <Dropdown.Item
                        href="#"
                        className="bs-small-font"
                        rel="nofollow"
                      >
                        todo: Parity Trace
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <div className="bs-tab-content">
                {[""].includes(tab) && (
                  <TransactionDetailsOverview
                    handleGetError={() => setIsgetError(true)}
                    setTabView={setTabView}
                  />
                )}
                {["tokenTransfer"].includes(tab) && tabView.tokenTransfers && (
                  <TransactionTokenTransfers />
                )}
                {["internaltx"].includes(tab) && <InternalTransactionList />}
                {["logs"].includes(tab) && !!transactionLogs?.length && (
                  <TransactionLogs transactionLogs={transactionLogs} />
                )}
                {["statechange"].includes(tab) && <TransactionState />}
                {["rawTrace"].includes(tab) && <TransactionRowTrace />}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionDetails;
